// Files on a record (a cost's invoice and payment receipt, a timeline entry's photos, a PO's PI): drop them on the box,
// paste them (screenshots too) or choose them; the full list is the usual table (FileTable), images and PDFs preview
// beside the row they are opened from. Uploads stream with a progress bar
// (the same way as spec files) to /api/attachments; staff only, as the server checks.
import { Download, Eye, FileText, ImageIcon, Paperclip, Trash2, X } from "lucide-react";
import { useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import type { Attachment } from "../../../shared/db.gen.ts";
import {
  ATTACHMENT_KIND_LABELS,
  ATTACHMENT_KINDS,
  ATTACHMENT_PROXY_LIMIT_MB,
  type AttachmentKind,
  type AttachmentOwner,
  guessAttachmentKind,
  MAX_ATTACHMENT_BYTES,
  viewableType,
} from "../../../shared/runs.ts";
import { shortDate, useAttachmentsByOwner } from "../lib/costs.ts";
import { plural } from "../lib/format.ts";
import { can, mutate } from "../lib/mutate.ts";
import { useRows } from "../lib/sync.ts";
import { bytes } from "../pages/specs/lib.ts";
import { cx } from "./controls.tsx";
import { type Column, DataTable } from "./DataTable.tsx";
import { DialogLayer } from "./DialogLayer.tsx";
import { DropFace, pastedName, useFileDrop } from "./FileDrop.tsx";
import { toast } from "./toast.tsx";

export const attachmentUrl = (id: string, inline = false) => `/api/attachments/${id}/download${inline ? "?inline=1" : ""}`;

type Upload = { key: string; name: string; loaded: number; total: number };
let uploads: Upload[] = [];
const listeners = new Set<() => void>();
const setUploads = (next: Upload[]) => {
  uploads = next;
  for (const fn of listeners) fn();
};
const useUploads = () =>
  useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => uploads,
  );

/** Streams one file to the server as an attachment of `owner`, with progress; resolves with its id. */
export function uploadAttachment(owner: { type: AttachmentOwner; id: string }, file: File, opts: { kind?: AttachmentKind; docDate?: string | null } = {}) {
  const id = crypto.randomUUID();
  const key = `${owner.type}:${owner.id}`;
  if (file.size > MAX_ATTACHMENT_BYTES)
    return Promise.reject(new Error(`${file.name} is ${bytes(file.size)}; attachments can be up to ${MAX_ATTACHMENT_BYTES / 1024 / 1024} MB`));
  const kind = opts.kind ?? guessAttachmentKind(file.name, file.type);
  const q = new URLSearchParams({ owner_type: owner.type, owner_id: owner.id, id, kind, ...(opts.docDate ? { doc_date: opts.docDate } : {}) });
  setUploads([...uploads, { key, name: file.name, loaded: 0, total: file.size }]);
  const set = (loaded: number) => setUploads(uploads.map((u) => (u.key === key && u.name === file.name ? { ...u, loaded } : u)));
  const done = () => setUploads(uploads.filter((u) => !(u.key === key && u.name === file.name)));
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/attachments?${q}`);
    xhr.setRequestHeader("x-requested-with", "hhl-ops");
    xhr.setRequestHeader("content-type", file.type || "application/octet-stream");
    xhr.setRequestHeader("x-filename", encodeURIComponent(file.name || "pasted file"));
    xhr.upload.onprogress = (e) => set(e.loaded);
    xhr.onload = () => {
      done();
      let body: { id?: string; error?: string } = {};
      try {
        body = JSON.parse(xhr.responseText);
      } catch {}
      if (xhr.status >= 200 && xhr.status < 300 && body.id) return resolve(body.id);
      // Production's web server (Caddy) refuses big bodies outside spec files until its attachments rule is added.
      if (xhr.status === 413)
        return reject(
          new Error(
            `${file.name} is over the ${ATTACHMENT_PROXY_LIMIT_MB} MB the web server lets through for attachments right now. Ask Mitch to add the /api/attachments/* upload rule (deploy/Caddyfile.snippet), or attach a smaller copy.`,
          ),
        );
      reject(new Error(body.error ?? `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => {
      done();
      reject(new Error(`Couldn't upload ${file.name}: the connection dropped`));
    };
    xhr.send(file);
  });
}

async function send(owner: { type: AttachmentOwner; id: string }, files: File[], opts: { kind?: AttachmentKind } = {}) {
  for (const f of files) {
    try {
      await uploadAttachment(owner, f, opts);
      toast.ok(`Attached ${f.name || "the pasted file"}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }
}

/**
 * Pasted files onto a record: a screenshot (the clipboard calls it "image.png") is named for when it was pasted,
 * "Pasted 2026-11-04 14:05.png", so a few of them read apart. For a record's own paste-anywhere (an open PO).
 */
export function attachPasted(owner: { type: AttachmentOwner; id: string }, files: File[], opts: { kind?: AttachmentKind } = {}) {
  return send(owner, files.map(pastedName), opts);
}

/**
 * The files on one record, with a drop/paste box. `compact` shows a one-line strip (a paperclip count and chips) for
 * table rows; the full list is the usual table (FileTable), each image or PDF previewing beside its row.
 */
export function Attachments({
  owner,
  compact,
  kind,
  hint,
  disabled,
}: {
  owner: { type: AttachmentOwner; id: string };
  compact?: boolean;
  kind?: AttachmentKind;
  hint?: string;
  disabled?: boolean;
}) {
  const all = useAttachmentsByOwner().get(`${owner.type}:${owner.id}`) ?? [];
  const pending = useUploads().filter((u) => u.key === `${owner.type}:${owner.id}`);
  const editable = !disabled && can("attachment.update");
  // The shared file box (ui/FileDrop.tsx): drop, paste (a screenshot named for when it was pasted) or choose
  const drop = useFileDrop({ onFiles: (files) => send(owner, files, { kind }), disabled: !editable });
  const [listing, setListing] = useState(false); // compact: every file, in the full box, from its "+N"

  if (compact)
    return (
      // Fits the cell it's in: the chips shrink (names truncated; the full name on hover), the paperclip stays.
      <span
        className={cx("inline-flex min-w-0 max-w-full items-center gap-1 rounded align-middle", drop.over && "outline-2 outline-accent outline-dashed")}
        onDragOver={drop.zone.onDragOver}
        onDragLeave={drop.zone.onDragLeave}
        onDrop={drop.zone.onDrop}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {all.slice(0, 2).map((a) => (
          <a
            key={a.id}
            href={attachmentUrl(a.id, viewableType(a.content_type))}
            target="_blank"
            rel="noreferrer"
            title={`${a.filename} · ${ATTACHMENT_KIND_LABELS[a.kind]} · ${bytes(a.size)}`}
            className="inline-flex h-5 min-w-0 max-w-28 shrink items-center gap-1 rounded bg-surface-2 px-1.5 text-[11px] text-muted hover:text-text"
          >
            {a.content_type.startsWith("image/") ? <ImageIcon className="size-3 shrink-0" /> : <FileText className="size-3 shrink-0" />}
            <span className="truncate">{a.filename}</span>
          </a>
        ))}
        {all.length > 2 && (
          <button
            type="button"
            title={`All ${all.length} files`}
            onClick={() => setListing(true)}
            className="shrink-0 rounded px-0.5 text-[11px] text-faint hover:bg-hover hover:text-text"
          >
            +{all.length - 2}
          </button>
        )}
        {listing &&
          // On the page itself, not inside the row (a table's rows are moved with transforms, which a fixed layer follows)
          createPortal(
            <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && setListing(false)}>
              <div
                className="w-[760px] max-w-[calc(100vw-32px)] rounded-xl border border-line bg-surface p-4 shadow-pop"
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.stopPropagation();
                    setListing(false);
                  }
                }}
              >
                <div className="mb-3 flex items-center gap-2">
                  <div className="min-w-0 flex-1 text-[15px] font-semibold">{plural(all.length, "file")}</div>
                  <button type="button" onClick={() => setListing(false)} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Close (Esc)">
                    <X className="size-4" />
                  </button>
                </div>
                <Attachments owner={owner} kind={kind} hint={hint} disabled={disabled} />
              </div>
            </DialogLayer>,
            document.body,
          )}
        {pending.map((u) => (
          <span key={u.name} className="text-[11px] text-accent-text">
            {Math.floor((u.loaded / Math.max(1, u.total)) * 100)}%
          </span>
        ))}
        {editable && (
          <button
            type="button"
            title="Attach a file (or drop one here)"
            onClick={drop.choose}
            className="shrink-0 rounded p-0.5 text-faint hover:bg-hover hover:text-text"
          >
            <Paperclip className="size-3.5" />
          </button>
        )}
        {drop.picker}
      </span>
    );

  return (
    <div tabIndex={editable ? -1 : undefined} {...drop.zone} className="flex flex-col gap-1 rounded-md focus:outline-none">
      {all.length > 0 && <FileTable files={all} editable={editable} />}
      {pending.map((u) => (
        <div key={u.name} className="rounded-md border border-line px-3 py-1.5">
          <div className="mb-0.5 flex justify-between text-[11px] text-muted">
            <span className="truncate">Uploading {u.name}</span>
            <span className="font-mono">
              {Math.floor((u.loaded / Math.max(1, u.total)) * 100)}% · {bytes(u.loaded)} of {bytes(u.total)}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded bg-surface-2">
            <div className="h-full bg-accent transition-[width]" style={{ width: `${(u.loaded / Math.max(1, u.total)) * 100}%` }} />
          </div>
        </div>
      ))}
      {editable ? (
        <DropFace
          over={drop.over}
          onClick={drop.choose}
          faceRef={drop.face}
          size="sm"
          title={hint ?? (all.length ? "Add more files" : "The invoice, receipt or photos")}
        />
      ) : (
        !all.length && <div className="px-1 py-1 text-[12px] text-faint">No files.</div>
      )}
      {drop.picker}
    </div>
  );
}

const KIND_OPTIONS = ATTACHMENT_KINDS.map((k) => ({ value: k, label: ATTACHMENT_KIND_LABELS[k] }));
const viewable = (a: Attachment) => a.content_type.startsWith("image/") || a.content_type === "application/pdf";

/**
 * A record's files in the usual table (views, columns, Copy / CSV): Kind, Note and Dated edit in their cells; a row (or
 * its eye) opens its preview beside it, download and remove at the row's end.
 */
function FileTable({ files: all, editable }: { files: Attachment[]; editable: boolean }) {
  const [open, setOpen] = useState<string | null>(null); // the file whose preview is open
  const users = useRows("app_user");
  const opened = all.find((a) => a.id === open);
  const byName = (a: Attachment) => {
    const who = users.find((u) => u.id === a.uploaded_by);
    return who ? who.name || who.email : "";
  };
  const save = (a: Attachment, patch: { kind?: AttachmentKind; note?: string; doc_date?: string | null }) => mutate("attachment.update", { id: a.id, patch });
  const columns: Column<Attachment>[] = [
    {
      key: "filename",
      header: "File",
      width: "minmax(160px,2fr)",
      cell: (a) => (
        <>
          {a.content_type.startsWith("image/") ? (
            <img src={attachmentUrl(a.id, true)} alt="" loading="lazy" className="size-6 shrink-0 rounded border border-line object-cover" />
          ) : (
            <span className="flex size-6 shrink-0 items-center justify-center rounded border border-line bg-surface-2 text-faint">
              <FileText className="size-3.5" />
            </span>
          )}
          <span className="truncate text-[12px] font-medium" title={a.filename}>
            {a.filename}
          </span>
        </>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      width: "110px",
      value: (a) => ATTACHMENT_KIND_LABELS[a.kind],
      cell: (a) => <span className="truncate">{ATTACHMENT_KIND_LABELS[a.kind]}</span>,
      edit: { options: KIND_OPTIONS, get: (a) => a.kind, save: (a, kind) => save(a, { kind: kind as AttachmentKind }) },
    },
    {
      key: "note",
      header: "Note",
      headerTitle: "What it is (optional)",
      width: "minmax(100px,1.5fr)",
      cell: (a) => (
        <span className="truncate text-muted" title={a.note}>
          {a.note}
        </span>
      ),
      edit: { get: (a) => a.note, save: (a, note) => save(a, { note }) },
    },
    {
      key: "doc_date",
      header: "Dated",
      headerTitle: "The document's own date",
      width: "96px",
      value: (a) => a.doc_date ?? "",
      cell: (a) => <span className="text-muted">{a.doc_date ? shortDate(a.doc_date) : ""}</span>,
      edit: {
        get: (a) => a.doc_date ?? "",
        save: (a, text) => {
          const t = text.trim();
          if (t && !/^\d{4}-\d{2}-\d{2}$/.test(t)) return toast.error("Use a date like 2026-12-31");
          save(a, { doc_date: t || null });
        },
      },
    },
    { key: "size", header: "Size", width: "80px", align: "right", optional: true, cell: (a) => <span className="text-muted">{bytes(a.size)}</span> },
    {
      key: "added",
      header: "Added",
      width: "96px",
      value: (a) => a.created_at,
      text: (a) => a.created_at.slice(0, 10),
      cell: (a) => <span className="text-muted">{shortDate(a.created_at)}</span>,
    },
    {
      key: "by",
      header: "By",
      width: "minmax(90px,1fr)",
      optional: true,
      value: byName,
      cell: (a) => <span className="truncate text-muted">{byName(a)}</span>,
    },
    {
      key: "actions",
      header: "",
      width: "88px",
      value: () => "",
      cell: (a) => (
        <span className="ml-auto flex items-center" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {viewable(a) && (
            <button
              type="button"
              onClick={() => setOpen(open === a.id ? null : a.id)}
              className="rounded p-1 text-muted hover:bg-hover hover:text-text"
              title={open === a.id ? "Close the preview" : "Preview"}
            >
              {open === a.id ? <X className="size-3.5" /> : <Eye className="size-3.5" />}
            </button>
          )}
          <a
            href={attachmentUrl(a.id)}
            download={a.filename}
            className="rounded p-1 text-muted hover:bg-hover hover:text-text"
            title={`Download ${a.filename}`}
          >
            <Download className="size-3.5" />
          </a>
          {editable && (
            <button
              type="button"
              className="rounded p-1 text-faint hover:bg-danger-soft hover:text-danger"
              title="Remove this file"
              onClick={() => confirm(`Remove ${a.filename}?`) && mutate("attachment.remove", { id: a.id })}
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
        </span>
      ),
    },
  ];
  return (
    <>
      <div className="flex items-center gap-2 text-[11px] text-faint">
        <span>{plural(all.length, "file")}</span>
      </div>
      <DataTable
        fit
        rows={all}
        columns={columns}
        defaultSort={{ key: "added" }}
        editing={editable}
        activeId={opened?.id}
        onOpen={(a) => viewable(a) && setOpen(a.id)}
        rowNoun={["file", "files"]}
        exportName="Files"
        pageFilters={false}
      />
      {opened && <Preview a={opened} onClose={() => setOpen(null)} />}
    </>
  );
}

/** An image or PDF, opened beside the row it was opened from. */
function Preview({ a, onClose }: { a: Attachment; onClose: () => void }) {
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="flex max-h-[84vh] w-[760px] max-w-[calc(100vw-32px)] flex-col rounded-xl border border-line bg-surface shadow-pop"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="flex items-center gap-2 border-b border-line px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium" title={a.filename}>
            {a.filename}
          </span>
          <a
            href={attachmentUrl(a.id)}
            download={a.filename}
            className="rounded p-1 text-muted hover:bg-hover hover:text-text"
            title={`Download ${a.filename}`}
          >
            <Download className="size-3.5" />
          </a>
          <button type="button" autoFocus onClick={onClose} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title="Close the preview (Esc)">
            <X className="size-3.5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-2">
          {a.content_type.startsWith("image/") ? (
            <img src={attachmentUrl(a.id, true)} alt={a.filename} className="max-h-[70vh] w-full rounded object-contain" />
          ) : (
            <iframe title={a.filename} src={attachmentUrl(a.id, true)} className="h-[70vh] w-full rounded border border-line bg-white" />
          )}
        </div>
      </div>
    </DialogLayer>
  );
}
